import { ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";

// Simple Badge component since I didn't create one in ui folder yet, or I can just use span
function ProjectBadge({ children }: { children: React.ReactNode }) {
    return (
        <span className="inline-flex items-center rounded-sm bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80">
            {children}
        </span>
    );
}



export default function Projects() {
    return (
        <section id="projects" className="py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col items-center text-center mb-16">
                    <div className="inline-block bg-neon text-black text-xs font-bold px-2 py-1 mb-4 uppercase tracking-wider">Selected Works</div>
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">Recent Projects</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {projects.map((project, index) => (
                        <div key={index} className="group relative rounded-2xl overflow-hidden bg-muted border border-border flex flex-col h-full">
                            {/* Image Area */}
                            <div className="aspect-video w-full overflow-hidden relative">
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <Button asChild size="sm" variant="secondary" className="font-bold">
                                        <a href={project.liveLink} target="_blank" rel="noopener noreferrer">
                                            Live Demo <ExternalLink className="ml-2 w-4 h-4" />
                                        </a>
                                    </Button>
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="p-6 flex flex-col flex-grow bg-card">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold mb-1">{project.title}</h3>
                                        <p className="text-xs text-neon font-mono uppercase tracking-wide">{project.category}</p>
                                    </div>
                                    <a href={project.liveLink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                                        <ExternalLink className="w-5 h-5" />
                                    </a>
                                </div>

                                <p className="text-muted-foreground text-sm mb-6 line-clamp-3 flex-grow">
                                    {project.description}
                                </p>

                                <div className="flex flex-wrap gap-2 mt-auto">
                                    {project.tech.map(t => (
                                        <ProjectBadge key={t}>{t}</ProjectBadge>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-16">
                    <Button variant="outline" size="lg">VIEW ALL PROJECTS</Button>
                </div>
            </div>
        </section>
    );
}

const projects = [
    {
        title: "Food Kart",
        category: "Web App",
        description: "AI-scale enterprise resource planning system designed for high-volume data processing and team collaboration.",
        tech: ["React and vite", "Typescript", "Firebase"],
        image: "/img/food.jpg",
        liveLink: "https://food-delivery-cyan-beta.vercel.app/"
    },
    {
        title: "World Flags Color",
        category: "World Flags / Web App",
        description: "Interactive application to explore and learn about world flags and their colors.",
        tech: ["Typescript", "Tailwind", "Vercel"],
        image: "/img/flag1.jpg",
        liveLink: "https://flagscolors.vercel.app/"
    },
    {
        title: "Visit Mogadishu",
        category: "Tourism",
        description: "A comprehensive guide for tourists visiting Mogadishu, featuring top attractions and local tips.",
        tech: ["React", "N8N", "framer-motion"],
        image: "/img/visit2.jpg",
        liveLink: "https://visit-mogadisho.vercel.app/"
    }
];
